import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { getProfileUrl } from '@/lib/profileUrl';
import type { NotificationItem } from '@/hooks/useNotifications';

interface CheckinLikeNotificationItemProps {
  notification: NotificationItem;
  isUnread: boolean;
  onClose: () => void;
}

export function CheckinLikeNotificationItem({ notification, isUnread, onClose }: CheckinLikeNotificationItemProps) {
  const navigate = useNavigate();
  const { data } = notification;

  const handleClick = () => {
    if (data.burger_id) {
      navigate(`/burger/${data.burger_id}`);
    }
    onClose();
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
        isUnread && "bg-primary/5 border-l-2 border-primary"
      )}
      onClick={handleClick}
    >
      {data.liker_avatar_url ? (
        <Avatar className="h-12 w-12 flex-shrink-0">
          <AvatarImage src={data.liker_avatar_url} />
          <AvatarFallback className="bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
            {getInitials(data.liker_name)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="h-12 w-12 rounded-full flex items-center justify-center bg-orange-100 dark:bg-orange-900/20 text-2xl flex-shrink-0">
          🔥
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-foreground text-sm",
          isUnread ? "font-semibold" : "font-medium"
        )}>
          {data.liker_name || 'Someone'} liked your check-in
          {data.burger_name && (
            <> of <span className="text-primary">{data.burger_name}</span></>
          )}
        </p>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
          <Clock className="h-3 w-3" />
          <span>
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>

      <span className="text-xl flex-shrink-0 self-center">🔥</span>
    </div>
  );
}
